import Vue from 'vue'
import http from '@/utils/http-common'
import { acquireGlobalOverlayOwnership } from '@/utils/httpOverlayPolicy.mjs'
import { getAuthIdentity, isAuthTransitioning, isCurrentAuthSession } from '@/utils/auth-storage-sync.mjs'

export const rpaDurationExecutionState = Vue.observable({ execution: null })
let activeRun = null

// Completion belongs to the original run request. The reserved token only
// connects that same execution to the read-only duration endpoint.
export async function runRpaWithDuration(vm, { rpaId, isAsync, config = {} }) {
  const url = `annotation/run-rpa/${rpaId}/`
  if (isAsync) return http.get(url, config)
  const route = vm.$route?.fullPath
  const user = vm.$store.state.user_info || {}
  const userId = user.pk ?? user.id, tenantId = user.tenant_id
  const entry = { rpaId, route, userId, tenantId, executionToken: '', status: 'running' }
  activeRun = entry
  const releaseOverlay = acquireGlobalOverlayOwnership()
  const identity = getAuthIdentity(vm.$store.state)
  let invalidated = false
  const contextKey = () => {
    const current = vm.$store.state.user_info || {}
    return JSON.stringify([vm.$route?.fullPath, current.pk ?? current.id, current.tenant_id,
      vm.loadedRpaId ?? vm.clickedRunRpa?.id ?? rpaId, vm.latestWorkflowRunDispatchToken,
      vm.workflowRunDispatchInFlight, Boolean(vm.$store.state.access_token)])
  }
  const originalContext = contextKey()
  const invalidate = () => {
    invalidated = true
    releaseOverlay()
    if (rpaDurationExecutionState.execution === entry) rpaDurationExecutionState.execution = null
  }
  const stopWatch = vm.$watch?.(contextKey, invalidate) || (() => {})
  vm.$once?.('hook:beforeDestroy', invalidate)
  const isCurrent = () => {
    if (invalidated || vm._isDestroyed || vm._isBeingDestroyed || contextKey() !== originalContext || isAuthTransitioning(vm.$store.state)) return false
    try { return typeof window === 'undefined' || isCurrentAuthSession(vm.$store.state, identity, window.localStorage) } catch { return false }
  }
  try {
    let token = null
    try {
      const response = await http.post('annotation/rpa-executions/', { rpa_id: rpaId }, {
        ...config, params: undefined, onWorkflowIssueReport: undefined, timeout: 10000,
        preserveGlobalOverlay: true, suppressGlobalErrorToast: true, preserveLicenseErrorHandling: true,
      })
      if (typeof response?.data?.execution_token === 'string' && response.data.execution_token) token = response.data.execution_token
    } catch (error) {
      if (error.code === 'AUTH_SESSION_CHANGED') { invalidate(); return { rpaExecutionHandled: true } }
      const status = error.response?.status
      if (error.code === 'ERR_CANCELED' || (status && ![404, 405].includes(status) && (status < 500 || status >= 600))) throw error
    }
    if (!isCurrent() || activeRun !== entry) return { rpaExecutionHandled: true }
    if (token) {
      entry.executionToken = token
      rpaDurationExecutionState.execution = entry
    }
    const response = await http.get(url, token ? { ...config, params: { ...config.params, execution_token: token } } : config)
    return isCurrent() ? response : { rpaExecutionHandled: true }
  } catch (error) {
    if (!isCurrent()) return { rpaExecutionHandled: true }
    throw error
  } finally {
    stopWatch()
    vm.$off?.('hook:beforeDestroy', invalidate)
    releaseOverlay()
    if (activeRun === entry) {
      activeRun = null
      rpaDurationExecutionState.execution = null
      if (isCurrent()) {
        vm.$store.commit?.('setOverlay', false)
        vm.$store.commit?.('setOverlayText', '')
      }
    }
  }
}
